import { AutoAwesome, Delete, Link as LinkIcon, MoreVert, Person, Public, Timeline } from '@mui/icons-material';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import CButton from '../../../components/CButton';
import CDeleteConfirmationButton from '../../../components/CDeleteConfirmationButton';
import CLocalizedText from '../../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CModal from '../../../components/CModal';
import CPageModule from '../../../components/CPageModule';
import CPageModulePaginatedTable from '../../../components/CPageModulePaginatedTable';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CStatefulContainer from '../../../components/CStatefulContainer';
import Page from '../../../components/layout/Page';
import XDDropdown from '../../../components/selection/XDDropdown';
import XDDropdownItem from '../../../components/selection/XDDropdownItem';
import { type EAppTypeValue } from '../../../enums/AppType';
import { EUserRoles } from '../../../enums/EUserRoles';
import { SourceType } from '../../../enums/SourceType';
import { PageRoutes } from '../../../enums/enums';
import addCustomTableColumn from '../../../helpers/addCustomTableColumn';
import formatTableColumn from '../../../helpers/formatTableColumn';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import type { IAugmentedBackendResponse } from '../../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../../models/IRequestParams';
import type { IResponse } from '../../../models/IResponse';
import type { ISortingMeta } from '../../../models/ISortingMeta';
import type { TPandasTable } from '../../../models/TPandasTable';
import { type IUsageTrackingInfo } from '../../../models/usage-tracking/IUsageTracking';
import { SvUsageTrackingServices } from '../../../services/SvUsageTrackingServices';
import { updateSnackbar } from '../../../store/appSlice';
import { updateUserObject } from '../../../store/userSlice';
import { userHasRole } from '../../../utils/utils';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
  tsOpen: boolean;
  tsFigure: any | null;
  tsTitle: string;
  modalOpen: boolean;
  recordToBeDeleted: any | null;
}

const SOURCE_TYPE_COL_INDEX = 0;
const SOURCE_IDENTIFIER_COL_INDEX = 1;
const TIMESERIES_COL_INDEX = 4;

export default function PSingleAppTracking() {
  const { appName } = useParams();
  const navigate = useNavigate();
  const { locale } = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();
  const isAdmin = userHasRole(keycloak.tokenParsed, [[EUserRoles.ADMIN]]);

  const defaultSorting: ISortingMeta = { sortCol: 2, ascending: 'descending' };
  const allAppTrackingTitle = CLocalizedTextStringStatic('usageTracking.title');
  const actionsLabel = CLocalizedTextStringStatic('usageTracking.table.columns.actions');

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    },
    tsOpen: false,
    tsFigure: null,
    tsTitle: 'Timeseries',
    modalOpen: false,
    recordToBeDeleted: null
  });

  const fetchAppUsage = async (page: number, sorting?: ISortingMeta, search?: string) => {
    if (!appName) {
      return;
    }

    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () =>
        await SvUsageTrackingServices.getAppOverView({
          appName,
          page,
          sorting: sorting ?? defaultSorting,
          search
        })
    );
    updateStateAttribute<State>(
      { snapshot: { ...(await getState()).snapshot, params: { sorting, search } } },
      setState
    );
  };

  useEffect(() => {
    const params = {
      sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
      ascending: (searchParams.get('ascending') as unknown as ISortingMeta['ascending']) ?? 'descending'
    };

    const page = Number.parseInt(searchParams.get('page') ?? '1');
    const search = searchParams.get('search') ?? undefined;

    (async () => {
      await fetchAppUsage(page, params, search);
    })();
  }, [locale]);

  const cols = Object.keys(state.snapshot.data?.results ?? {});
  const tsKey = cols[TIMESERIES_COL_INDEX];

  const tableWithPlot: TPandasTable | undefined =
    tsKey && state.snapshot.data?.results
      ? formatTableColumn(state.snapshot.data.results, TIMESERIES_COL_INDEX, (item: any) => {
        const title = appName;

        return (
          <div className="w-full flex items-center justify-center">
            <CButton
              propagateClick={false}
              style={{ borderWidth: 1 }}
              onClick={(e) => {
                e.preventDefault();
                updateStateAttribute<State>({ tsOpen: true, tsFigure: item, tsTitle: title }, setState);
              }}
            >
              <Timeline />
            </CButton>
          </div>
        );
      })
      : state.snapshot.data?.results;

  const tableWithDeleteButton: TPandasTable | undefined =
    Object.keys(tableWithPlot ?? {}).length === 0
      ? tableWithPlot
      : isAdmin
        ? addCustomTableColumn(tableWithPlot, actionsLabel, (_rowIndex, rowArray) => {
          return (
            <div className="flex justify-center">
              <XDDropdown
                usePortal
                closeOnAnyClick
                button={
                  <CButton style={{ borderWidth: 1 }}>
                    <MoreVert />
                  </CButton>
                }
              >
                <div className="flex flex-col">
                  <XDDropdownItem
                    data-no-rowlink
                    className="justify-center text-[14px]"
                    onClick={() => {
                      updateStateAttribute<State>({ modalOpen: true, recordToBeDeleted: rowArray }, setState);
                    }}
                  >
                    <Delete />
                    <CLocalizedText dictKey="global.deleteButton" />
                  </XDDropdownItem>
                </div>
              </XDDropdown>
            </div>
          );
        })
        : tableWithPlot;

  const tableWithButtonIdentifier: TPandasTable | undefined = formatTableColumn(
    tableWithDeleteButton,
    SOURCE_IDENTIFIER_COL_INDEX,
    (item, row) => {
      switch (row[SOURCE_TYPE_COL_INDEX]) {
        case SourceType.Keycloak: {
          const jsonObj = JSON.parse(item ?? '');
          const fullName = [jsonObj.firstName, jsonObj.lastName].filter((e) => !!e).join(' ');
          const isSystem = jsonObj.firstName.toLowerCase() === 'system';
          const disableClick = isSystem || jsonObj.email === '';

          return (
            <CButton
              icons={{ start: isSystem ? <AutoAwesome /> : <Person /> }}
              onClick={() => {
                dispatch(updateUserObject(jsonObj));
                navigate(
                  `/${PageRoutes.USERS_OUTLET}/${PageRoutes.USERS_OVERVIEW_OUTLET}/${PageRoutes.USERS_USER_HUB_OUTLET}`
                );
              }}
              disabled={disableClick}
            >
              {fullName}
            </CButton>
          );
        }
        case SourceType.Factsheet:
          return (
            <Link
              to={`/${PageRoutes.PUBLISHING_OUTLET}/${PageRoutes.PUBLISHING_FS_OUTLET}/${PageRoutes.PUBLISHING_FS_OVERVIEW_OUTLET}/${item}`}
              className={'w-full flex flex-col'}
            >
              <CButton icons={{ start: <LinkIcon /> }}>{item}</CButton>
            </Link>
          );
        case SourceType.Visitor:
          return (
            <CButton icons={{ start: <Public /> }} disabled>
              {item}
            </CButton>
          );
        default:
          return item;
      }
    }
  );

  const augmentedData = state.snapshot.data
    ? { ...state.snapshot.data, results: tableWithButtonIdentifier ?? state.snapshot.data.results }
    : undefined;

  const deleteHandler = async () => {
    const sourceType = state.recordToBeDeleted?.[SOURCE_TYPE_COL_INDEX] as SourceType;
    let sourceIdentifier = state.recordToBeDeleted?.[SOURCE_IDENTIFIER_COL_INDEX] as string | null;

    switch (sourceType) {
      case SourceType.Keycloak:
        sourceIdentifier = JSON.parse(state.recordToBeDeleted?.[SOURCE_IDENTIFIER_COL_INDEX]).id;
        break;
      default:
        break;
    }

    const params: IUsageTrackingInfo = {
      app: appName as EAppTypeValue,
      sourceType,
      sourceIdentifier
    };

    const res = await SvUsageTrackingServices.deleteUsageTrackingNonAppWide(params);

    if (!res.hasError()) {
      // Re-fetch data with current params (keeps pagination, sorting, search)
      const { params } = state.snapshot;
      await fetchAppUsage(Number(searchParams.get('page') ?? '1'), params?.sorting ?? defaultSorting, params?.search);
    }

    dispatch(
      updateSnackbar({
        message: res.getData().message,
        severity: res.hasError() ? 'error' : 'success',
        isVisible: true
      })
    );
  };

  return (
    <Page title={`${appName}`}>
      <CPageModule>
        <h1>
          {allAppTrackingTitle} ({appName})
        </h1>
      </CPageModule>
      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CStatefulContainer submodule apiRequestState={state.snapshot.state} showContent={!!state.snapshot.data}>
          <CPageModulePaginatedTable
            state={state.snapshot.state}
            data={augmentedData}
            params={state.snapshot.params}
            searchBarName={'appSearch'}
            searchBarPlaceholder={CLocalizedTextStringStatic('usageTracking.searchByIdentifier')}
            colSpanArr={[1, 3, 1, 1, 0.8, 0.8]}
            colAlignmentArr={['left', 'left', 'right', 'right', 'center', 'center']}
            colFormatArr={[
              undefined,
              undefined,
              { in: 'tzTimestamp', out: 'localeDateString' },
              undefined,
              undefined,
              undefined
            ]}
            onChange={async (page, sorting, search) => {
              await fetchAppUsage(page, sorting, search);
              searchParams.set('page', page.toString());
              searchParams.set('sortCol', sorting.sortCol.toString());
              searchParams.set('ascending', sorting.ascending);
              searchParams.set('search', search);
              setSearchParams(searchParams);
            }}
          />
        </CStatefulContainer>
      </CPageModule>
      <CModal
        open={state.tsOpen}
        onClose={() => {
          updateStateAttribute<State>({ tsOpen: false, tsFigure: null }, setState);
        }}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      >
        <CPageModule style={{ paddingTop: 0 }}>
          <h3>{state.tsTitle}</h3>
        </CPageModule>
        <CPageModulePlot disablePlotModal data={state.tsFigure?.data} layout={state.tsFigure?.layout} />
      </CModal>
      <CDeleteConfirmationButton
        showModal={state.modalOpen}
        onConfirm={async () => {
          await deleteHandler();
        }}
        onCancel={() => {
          updateStateAttribute<State>({ modalOpen: false, recordToBeDeleted: null }, setState);
        }}
        title={CLocalizedTextStringStatic('deleteRecord.confirmationTitle')}
      />
    </Page>
  );
}